import type { CircleReviewSource } from "@circle-react/types/CommunityReview";
import { useMemo } from "react";

import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";

// `rating` is the dialog before a star is chosen, `details` the expanded form
// with tags and description.
export type ReviewModalStep = "rating" | "details";

export interface ReviewDismissContext {
  hasDraft: boolean;
  step: ReviewModalStep;
}

interface ReviewSubmitFields {
  descriptionLength: number;
  rating: number | undefined;
  tagKeys: string[];
}

/**
 * Product analytics for the Circle review surfaces (banner, invitation and the
 * review form). Event names are registered in `hooks/analytics/reviewsTypes.ts`.
 *
 * `community_id` and the member id travel with every event, `useEventsTracker`
 * adds them, so they are not repeated here. Description text is never sent,
 * only its length.
 */
export const useReviewsTracker = () => {
  const { trackEvent } = useEventsTracker();

  return useMemo(
    () => ({
      // `review_banner_viewed` and `review_prompt_modal_viewed` are the
      // denominator of the dashboard's response rate.
      trackBannerViewed: () => {
        trackEvent("review_banner_viewed", {});
      },
      trackBannerDismissed: () => {
        trackEvent("review_banner_dismissed", {});
      },
      trackPromptModalViewed: () => {
        trackEvent("review_prompt_modal_viewed", {});
      },
      trackPromptModalDismissed: () => {
        trackEvent("review_prompt_modal_dismissed", {});
      },
      trackModalOpened: (source: CircleReviewSource) => {
        trackEvent("review_modal_opened", { source });
      },
      trackModalDismissed: (
        source: CircleReviewSource,
        { hasDraft, step }: ReviewDismissContext,
      ) => {
        trackEvent("review_modal_dismissed", {
          source,
          step,
          has_draft: hasDraft,
        });
      },
      trackCancelClicked: (step: ReviewModalStep) => {
        trackEvent("review_cancel_clicked", { step });
      },
      trackStarClicked: (rating: number) => {
        trackEvent("review_star_clicked", { rating });
      },
      trackTagClicked: (tagKey: string, selected: boolean) => {
        trackEvent("review_tag_clicked", { tag_key: tagKey, selected });
      },
      // `valid: false` answers how many members try to submit without enough
      // text, as long as the submit button can be pressed in that state.
      trackSubmitClicked: (
        { descriptionLength, rating, tagKeys }: ReviewSubmitFields,
        valid: boolean,
        source: CircleReviewSource,
      ) => {
        trackEvent("review_submit_clicked", {
          rating,
          tag_keys: tagKeys,
          description_length: descriptionLength,
          valid,
          source,
        });
      },
      trackSubmitSucceeded: (
        { descriptionLength, rating, tagKeys }: ReviewSubmitFields,
        reviewId: number | null,
        source: CircleReviewSource,
      ) => {
        trackEvent("review_submit_succeeded", {
          review_id: reviewId,
          rating,
          tag_keys: tagKeys,
          description_length: descriptionLength,
          source,
        });
      },
      trackSubmitFailed: (errorCode: string, source: CircleReviewSource) => {
        trackEvent("review_submit_failed", { error_code: errorCode, source });
      },
      trackAdminNudgeViewed: () => {
        trackEvent("admin_review_nudge_viewed", {});
      },
      trackAdminNudgeDismissed: () => {
        trackEvent("admin_review_nudge_dismissed", {});
      },
      trackAdminNudgeLearnMoreClicked: () => {
        trackEvent("admin_review_nudge_learn_more_clicked", {});
      },
      trackAdminNudgeCollectReviewsClicked: () => {
        trackEvent("admin_review_nudge_collect_reviews_clicked", {});
      },
    }),
    [trackEvent],
  );
};
